import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { RangeQueryInterface } from '@sport-activities/sdk';

import { UpdateFilterPayloadInterface } from '~/attendee/interfaces/update-filter-payload.interface';
import { CtaVariantEnum } from '~/component/call-to-action/enums/variant.enum';
import { propertyHasBeenEmptied } from '~/core/helpers/lifecycle-hooks/on-changes/property-has-been-emptied.helper';

const FIELD_NAME = 'ageRange';

@Component({
  selector: 'app-attendee-age-filter',
  templateUrl: './age-filter.component.html',
})
export class AgeFilterComponent implements OnInit, OnChanges {
  // default range vale
  @Input() public defaultRange?: RangeQueryInterface<number>;

  // emit filter value
  @Output() public selected = new EventEmitter<
    UpdateFilterPayloadInterface<RangeQueryInterface<number>>[]
  >();

  public readonly ctaVariant = CtaVariantEnum;
  public ageForm: FormArray;

  // maybe move this to unleash to let the country manage their list ?
  public ageRangeConfig = [
    {
      label: 'attendee.age_range.modal.less_3yo.label',
      values: {
        gte: 0,
        lte: 3,
      },
    },
    {
      label: 'attendee.age_range.modal.3_6yo.label',
      values: {
        gte: 3,
        lte: 6,
      },
    },
    {
      label: 'attendee.age_range.modal.7_11yo.label',
      values: {
        gte: 7,
        lte: 11,
      },
    },
    {
      label: 'attendee.age_range.modal.12_17yo.label',
      values: {
        gte: 12,
        lte: 17,
      },
    },
    {
      label: 'attendee.age_range.modal.18yo.label',
      values: {
        gte: 18,
        lte: 100,
      },
    },
  ];

  public hasSelectedFilters: boolean;

  constructor(private readonly formBuilder: FormBuilder) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (propertyHasBeenEmptied(changes, 'defaultRange')) {
      this.reset();
    }
  }

  public ngOnInit(): void {
    // setup form
    this.ageForm = this.formBuilder.array([]);
    this.ageRangeConfig.forEach((a) => {
      this.ageForm.push(
        new FormGroup({
          range: new FormControl(a),
          checked: new FormControl(false),
        })
      );
    });

    // parse default value
    if (!this.defaultRange) {
      return;
    }

    const { gte, lte } = this.defaultRange;

    this.ageForm.controls.forEach((a: FormGroup) => {
      const range = a.value.range.values;
      // check value if within wanted age range
      if (range?.gte >= gte && range?.lte <= lte) {
        a.controls.checked.setValue(true);
      }
    });

    this.setChipSelectedState();
  }

  public reset(): void {
    // reset checkboxes
    this.ageForm.controls.forEach((a: FormGroup) => {
      a.controls.checked.setValue(false);
    });

    // emit empty filter
    this.selected.emit([{ field: FIELD_NAME, value: null, type: 'RANGE' }]);

    this.setChipSelectedState();
  }

  public setChipSelectedState(): void {
    this.hasSelectedFilters = Boolean(
      this.ageForm.value.find((age) => age.checked)
    );
  }

  public update(): void {
    // Reset filter if no box checked
    let filterValue = null;

    const ageForm = this.ageForm.value.filter((age) => age.checked);

    if (ageForm.length > 0) {
      // get mini and max value from form
      const minimums = ageForm.map((ageRange) => {
        return ageRange?.range?.values?.gte || 1;
      });

      const maximums = ageForm.map((ageRange) => {
        return ageRange.range.values?.lte || 99;
      });

      filterValue = {
        gte: Math.min(...minimums),
        lte: Math.max(...maximums),
      };
    }

    // emit filter values
    this.selected.emit([
      {
        field: FIELD_NAME,
        value: filterValue,
        type: 'RANGE',
      },
    ]);

    this.setChipSelectedState();
  }
}
